import React, { useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import { cardClass, borderGrey, secondaryText } from '../../../../../styling/stylingHelper';

/**
 * Hover toolbar around one wide block (chart / table). The only control is a client-only
 * "Full width" toggle that breaks the block out of the `max-w-4xl` transcript column so a dense
 * table or a many-series chart gets the whole admin content width.
 *
 * Purely presentational: no re-hydration, no server call, and the state dies with the page.
 * Below `lg` the toggle is a no-op — blocks already span the full viewport there.
 */
export const BlockFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [fullWidth, setFullWidth] = useState(false);

  return (
    <div
      className={cn(
        'group relative transition-[width]',
        // Break out of the centered transcript column, capped to the admin content width.
        fullWidth && 'lg:relative lg:left-1/2 lg:-translate-x-1/2 lg:w-[min(77rem,calc(100vw-3rem))]'
      )}
    >
      <button
        type="button"
        onClick={() => setFullWidth((v) => !v)}
        aria-label={fullWidth ? t('cloudAgent.block.exitFullWidth') : t('cloudAgent.block.fullWidth')}
        title={fullWidth ? t('cloudAgent.block.exitFullWidth') : t('cloudAgent.block.fullWidth')}
        className={cn(
          'absolute -top-2 right-2 z-10 rounded-lg border p-1.5 shadow-sm transition',
          cardClass,
          borderGrey,
          secondaryText,
          'opacity-0 group-hover:opacity-100 focus:opacity-100',
          'hover:text-purple-600 dark:hover:text-purple-300',
          // Hidden below lg: the breakout it toggles is lg-only, so on a phone it was a control
          // that occupied space over the block and did nothing.
          'hidden lg:block'
        )}
      >
        {fullWidth ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
      </button>
      {children}
    </div>
  );
};

export default BlockFrame;
